///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Constants } from "@/constants"
import { type Database } from "@/earthbrowser/database/Database"
import { DatasetSeries } from "@/earthbrowser/datasets/DatasetSeries"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { HexGridDataFeature } from "@/earthbrowser/dataset-distribution/aurora/HexGridDataFeature"
import { HexGridDataFrame, type AuroraArrowData } from "@/earthbrowser/dataset-distribution/aurora/HexGridDataFrame"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { DatasetLayer, type ArrowRow, type DatasetSceneObject, type DatasetFeature, type DatasetManifest, type DatasetSyncConfig } from "@/earthbrowser/dataset-distribution/DatasetLayer"
import { AuroraTable, type AuroraProperties } from "./AuroraTable"
import { time, timedelta, timerange } from "@stovokor/math/time"
import { latLngToCell } from "h3-js"

const cHexGridResolution = 3
const cAuroraMultiplier = 1.0

// 5-minute OVATION frames, newest first; the feed runs a short way ahead of
// now (it's a forecast). Tier frame limits per the Phase 4 depth gates
// (SUBSCRIPTION_PRICING_MODEL.md): paid 48 hours, free 2 hours of history
// plus the forecast frames (short-range forecasts stay free). Hex grid data
// is fetched per-frame on demand, so this mainly sizes the timeline.
const cPaidFrameLimit = 576
const cFreeFrameLimit = 36

/** Effective frame limit for the current entitlement tier */
function frameLimit(): number {
	// No store (embed builds) means free-tier caps
	return isPaidEntitlement() ? cPaidFrameLimit : cFreeFrameLimit
}

/**
 * AuroraDatasetLayer - Manages aurora forecast visualization
 *
 * This layer:
 * - Creates and manages the HexGridDataFeature for aurora rendering
 * - Loads aurora entries from the AuroraTable (synced via manifest/checkpoint)
 * - Creates HexGridDataFrame objects for each aurora forecast
 * - Handles visibility control
 * - Provides refresh capability when new data is synced
 */
export class AuroraDatasetLayer extends DatasetLayer<DatasetFeature> {
	private hexGridFeature: HexGridDataFeature
	private dataSeries = new DatasetSeries<HexGridDataFrame>()
	private unsubscribe?: () => void
	// Last seen entitlement tier, to re-query frames when depth changes
	// (e.g. checkout completes and the 48-hour window unlocks live)
	private lastPaidState = false

	constructor(context: EarthBrowserContext) {
		super(context, "aurora")

		// Aurora frames are 5 minutes apart; the DatasetSeries default frequency (1 hour)
		// would skip update() re-evaluation for up to 30 minutes, leaving the layer
		// invisible (or stale) long after data becomes available for the current time
		this.dataSeries.frequency = timedelta.minutes(5)

		// Frames re-fetch their vertex data from the network on load, so drop
		// data outside the current window; otherwise scrubbing the 48-hour
		// timeline accumulates ~68MB of decompressed vertex arrays (576 x 121KB)
		this.dataSeries.purgeUnused = true

		// Create the hex grid feature for rendering
		this.hexGridFeature = new HexGridDataFeature(context, cHexGridResolution, this.dataSeries)

		// Add as child node for rendering
		this.addNode(this.hexGridFeature)

		// Subscribe to Redux for visibility and color palette changes
		this.subscribeToRedux()
	}

	/**
	 * Subscribe to Redux store for visibility changes
	 */
	private subscribeToRedux(): void {
		this.applyVisibility(engineBridge.getState()?.earthbrowser.visibleDatasets.includes("aurora") ?? false)
		this.lastPaidState = isPaidEntitlement()

		this.unsubscribe = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			if (!state) return
			const isVisible = state.earthbrowser.visibleDatasets.includes("aurora")
			if (isVisible !== this.visible) {
				this.applyVisibility(isVisible)
			}
			if (state.entitlements.isPaid !== this.lastPaidState) {
				this.lastPaidState = state.entitlements.isPaid
				void this.refresh()
			}
		})
	}

	/**
	 * Public visibility control, matching the overlay layers' setVisible.
	 * The app drives visibility through the engine-bridge subscription; the
	 * embed (no store) sets it directly from the URL params.
	 */
	public setVisible(visible: boolean): void {
		if (visible !== this.visible) {
			this.applyVisibility(visible)
		}
	}

	/**
	 * Apply visibility state
	 */
	private applyVisibility(visible: boolean): void {
		this.visible = visible
		this.hexGridFeature.visible = visible
		if (!visible) {
			// Release decompressed vertex data while hidden; frames re-fetch
			// from the network cache when the layer is re-enabled
			this.dataSeries.purgeAll()
		}
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "aurora",
			pollIntervalMs: 5 * 60_000, // 5 minutes (aurora updates every ~5 min from NOAA)
			needsSync: true,
			supportsDelta: false,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await AuroraTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[AuroraDatasetLayer] Downloading shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[AuroraDatasetLayer] Parsed ${rows.length} rows`)

		const auroras = this.parseAuroraRows(rows)
		await AuroraTable.replaceAll(auroras)
	}

	public async onSyncComplete(): Promise<void> {
		// Refresh the data frames from the database
		await this.refresh()
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.dataSeries.timeRange
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		return {
			frameTimesMs: this.dataSeries.frameTimesMs(),
			loadedRanges: this.dataSeries.loadedRangesMs(),
		}
	}

	protected override frameLoadState(): LayerLoadState | null {
		return this.dataSeries.loadState()
	}

	private parseAuroraRows(rows: ArrowRow[]): AuroraProperties[] {
		return rows.map(r => ({
			forecast_time: String(r.forecast_time),
			data_url: `${Constants.baseStorageUrl}/${String(r.data_path)}`,
			min_value: Number(r.min_value),
			max_value: Number(r.max_value),
			vertex_count: Number(r.vertex_count),
			grid_resolution: Number(r.grid_resolution),
		}))
	}

	// ==================== Data Management ====================

	/**
	 * Refresh aurora data from the database and update frames.
	 * Always loads the full window: DatasetSeries.setFrames replaces the
	 * frame list (keeping existing frames by url), so passing only the new
	 * rows collapsed the 48-hour series to one or two frames on every poll.
	 */
	public override async refresh(): Promise<void> {
		try {
			const auroras = await AuroraTable.getRecent(frameLimit())
			if (auroras.length > 0) {
				const frames = this.createFrames(auroras)
				this.dataSeries.setFrames(frames)
			}
		} catch (error) {
			console.error("[AuroraDatasetLayer] Failed to refresh:", error)
		}
	}

	/**
	 * Create HexGridDataFrame objects from aurora entries
	 */
	private createFrames(auroras: AuroraProperties[]): HexGridDataFrame[] {
		const frames: HexGridDataFrame[] = []

		// Auroras are sorted desc by time, we want chronological order
		const sortedAuroras = [...auroras].reverse()

		for (const aurora of sortedAuroras) {
			const startTime = new time(aurora.forecast_time)
			const delta = timedelta.minutes(5)
			const range = new timerange(startTime, startTime.adding(delta))

			const frame = new HexGridDataFrame(range, cAuroraMultiplier, aurora.forecast_time)

			const arrowData: AuroraArrowData = {
				forecast_time: aurora.forecast_time,
				data_url: aurora.data_url,
				min_value: aurora.min_value,
				max_value: aurora.max_value,
				vertex_count: aurora.vertex_count,
				grid_resolution: aurora.grid_resolution,
			}
			frame.setArrowData(arrowData)

			frames.push(frame)
		}

		return frames
	}

	/**
	 * Get the HexGridDataFeature for external access (e.g., palette settings)
	 */
	public getHexGridFeature(): HexGridDataFeature {
		return this.hexGridFeature
	}

	/**
	 * Sample the aurora probability (%) at a lat/lon from the current forecast
	 * frame. Ingest stores raw OVATION probabilities as uint8 (multiplier 1),
	 * so the vertex byte is the probability directly.
	 *
	 * Advances the data series to the scene clock so sampling works (and lazily
	 * loads the current frame) even while the aurora overlay itself is hidden.
	 * Returns null when no frame data is available yet or there is no aurora
	 * signal at that location.
	 */
	public sampleProbabilityAt(latDeg: number, lonDeg: number): number | null {
		this.dataSeries.update((this.context).clock.clockTime)
		const frame = this.dataSeries.currentFrame
		if (!frame?.vertexData) return null

		const cell = latLngToCell(latDeg, lonDeg, cHexGridResolution)
		const vertexIndices = this.hexGridFeature.hexGridGeometry.cellToVertexIndices.get(cell)
		if (!vertexIndices || vertexIndices.length === 0) return null

		// First index is the cell-center vertex
		const probability = frame.vertexData[vertexIndices[0]]
		return probability > 0 ? probability : null
	}

	// ==================== Scene Objects (not used for aurora) ====================

	public createSceneObject(_feature: DatasetFeature): DatasetSceneObject<DatasetFeature> | undefined {
		// Aurora doesn't use individual scene objects - it uses the hex grid feature
		return undefined
	}

	override dispose(): void {
		if (this.unsubscribe) {
			this.unsubscribe()
		}
		super.dispose()
	}
}

